import React from 'react';
import { Target, MapPin, DollarSign, TrendingUp } from 'lucide-react';

export default function KanbanMockup({ size = 'sm' }) {
  const isLg = size === 'lg';

  const stages = [
    {
      name: 'Identified',
      color: 'bg-slate-500',
      headerBg: 'bg-slate-500/15',
      borderColor: 'border-slate-500/30',
      cards: [
        { address: '88 Pine Ave', price: '$165K', profit: '$28K' },
        { address: '55 Oak Dr', price: '$142K', profit: '$19K' },
      ],
    },
    {
      name: 'Underwriting',
      color: 'bg-blue-500',
      headerBg: 'bg-blue-500/15',
      borderColor: 'border-blue-500/30',
      cards: [{ address: '301 Maple Ln', price: '$195K', profit: '$41K' }],
    },
    {
      name: 'CFO Review',
      color: 'bg-amber-500',
      headerBg: 'bg-amber-500/15',
      borderColor: 'border-amber-500/30',
      cards: [
        { address: '742 Birch St', price: '$220K', profit: '$55K' },
        { address: '19 Cedar Way', price: '$178K', profit: '$33K' },
      ],
    },
    {
      name: 'Acquired',
      color: 'bg-emerald-500',
      headerBg: 'bg-emerald-500/15',
      borderColor: 'border-emerald-500/30',
      cards: [{ address: '1428 Elm St', price: '$185K', profit: '$47K' }],
    },
  ];

  return (
    <div className={`w-full ${isLg ? 'p-4' : 'p-3'} bg-slate-800/80 rounded-xl`}>
      {/* Mini header */}
      <div className="flex items-center gap-2 mb-3">
        <div className="w-5 h-5 rounded bg-rose-500/20 flex items-center justify-center">
          <Target size={11} className="text-rose-400" />
        </div>
        <span className={`${isLg ? 'text-xs' : 'text-[10px]'} font-semibold text-white`}>
          Lead Pipeline
        </span>
        {isLg && (
          <span className="ml-auto text-[9px] bg-slate-700/60 text-slate-400 px-2 py-0.5 rounded-full font-medium">
            6 active leads
          </span>
        )}
      </div>

      {/* Kanban columns */}
      <div className={`grid grid-cols-4 ${isLg ? 'gap-2.5' : 'gap-1.5'}`}>
        {stages.map((stage, i) => (
          <div
            key={i}
            className={`bg-slate-700/30 rounded-lg overflow-hidden border ${stage.borderColor}`}
          >
            {/* Column header */}
            <div
              className={`${stage.headerBg} ${isLg ? 'px-2 py-1.5' : 'px-1.5 py-1'} flex items-center gap-1`}
            >
              <div className={`w-1.5 h-1.5 rounded-full ${stage.color}`} />
              <span className={`${isLg ? 'text-[9px]' : 'text-[7px]'} font-semibold text-white`}>
                {stage.name}
              </span>
              <span className={`ml-auto text-[7px] font-medium text-slate-500`}>
                {stage.cards.length}
              </span>
            </div>

            {/* Cards */}
            <div className={`${isLg ? 'p-1.5 space-y-1.5' : 'p-1 space-y-1'}`}>
              {stage.cards.slice(0, isLg ? 2 : 1).map((card, j) => (
                <div
                  key={j}
                  className={`bg-slate-800/60 rounded-md ${isLg ? 'p-2' : 'p-1.5'} border border-white/[0.04]`}
                >
                  <div className="flex items-center gap-0.5 mb-1">
                    <MapPin size={7} className="text-slate-500" />
                    <span
                      className={`${isLg ? 'text-[9px]' : 'text-[7px]'} font-semibold text-white truncate`}
                    >
                      {card.address}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-0.5">
                      <DollarSign size={7} className="text-slate-500" />
                      <span className="text-[7px] text-slate-400">{card.price}</span>
                    </div>
                    <div className="flex items-center gap-0.5">
                      <TrendingUp size={7} className="text-emerald-400" />
                      <span className="text-[7px] font-bold text-emerald-400">{card.profit}</span>
                    </div>
                  </div>
                </div>
              ))}
              {/* Overflow indicator */}
              {stage.cards.length > (isLg ? 2 : 1) && (
                <div className="text-center">
                  <span className="text-[6px] text-slate-600">
                    +{stage.cards.length - (isLg ? 2 : 1)} more
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Pipeline summary (lg only) */}
      {isLg && (
        <div className="mt-3 grid grid-cols-3 gap-2">
          <div className="bg-slate-700/40 rounded-lg px-2 py-1.5 text-center">
            <p className="text-[7px] text-slate-500 uppercase">Pipeline Value</p>
            <p className="text-xs font-extrabold text-white">$1.08M</p>
          </div>
          <div className="bg-slate-700/40 rounded-lg px-2 py-1.5 text-center">
            <p className="text-[7px] text-slate-500 uppercase">Est. Total Profit</p>
            <p className="text-xs font-extrabold text-emerald-400">$223K</p>
          </div>
          <div className="bg-slate-700/40 rounded-lg px-2 py-1.5 text-center">
            <p className="text-[7px] text-slate-500 uppercase">Avg. Close Rate</p>
            <p className="text-xs font-extrabold text-blue-400">68%</p>
          </div>
        </div>
      )}
    </div>
  );
}
